import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { ApplicationNote } from '../services/jobApplicationService'
import './NoteForm.css'

interface NoteFormProps {
  note?: ApplicationNote
  onSave: (note: ApplicationNote) => void | Promise<void>
  onCancel?: () => void
}

export const NoteForm: React.FC<NoteFormProps> = ({ note, onSave, onCancel }) => {
  const [activity, setActivity] = useState<string>(note?.activity || '')
  const [contactPerson, setContactPerson] = useState<string>(note?.contactPerson || '')
  const [contactInfo, setContactInfo] = useState<string>(note?.contactInfo || '')

  // Update form fields when note prop changes (for editing)
  useEffect(() => {
    if (note) {
      setActivity(note.activity || '')
      setContactPerson(note.contactPerson || '')
      setContactInfo(note.contactInfo || '')
    } else {
      // Reset form when not editing
      setActivity('')
      setContactPerson('')
      setContactInfo('')
    }
  }, [note])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const noteToSave: ApplicationNote = {
      id: note?.id || crypto.randomUUID(),
      timestamp: note?.timestamp || new Date().toISOString(),
      activity: activity.trim() || undefined,
      contactPerson: contactPerson.trim() || undefined,
      contactInfo: contactInfo.trim() || undefined
    }
    
    try {
      await onSave(noteToSave)
      
      // Reset form if not editing
      if (!note) {
        setActivity('')
        setContactPerson('')
        setContactInfo('')
      }
    } catch (error) {
      toast.error(`Failed to save note: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  return (
    <form className="note-form" onSubmit={handleSubmit}>
      <div className="note-form-fields">
        <div className="note-form-row note-form-row-top">
          <div className="note-form-field note-form-field-activity">
            <label htmlFor="activity">Activity</label>
            <textarea
              id="activity"
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              placeholder="What happened? (e.g., Talked to recruiter about next steps)"
            />
          </div>
          
          <div className="note-form-right-column">
            <div className="note-form-field note-form-field-contact-info">
              <label htmlFor="contactInfo">Contact Information</label>
              <input
                type="text"
                id="contactInfo"
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="Email, phone, or other contact details"
              />
            </div>
            
            <div className="note-form-field note-form-field-contact-person">
              <label htmlFor="contactPerson">Contact Person</label>
              <input
                type="text"
                id="contactPerson"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                placeholder="Name of person you talked to"
              />
            </div>
            
            <div className="note-form-actions">
              {onCancel && (
                <button type="button" className="note-form-cancel" onClick={onCancel}>
                  Cancel
                </button>
              )}
              <button type="submit" className="note-form-save">
                {note ? 'Update Note' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  )
}

